import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, Clock, Phone, User, MessageSquare, Loader2, CheckCircle, AlertCircle, Info, Building2, Video, Send, HardDrive, Cloud, FolderSync, UserCheck, UserPlus } from 'lucide-react';
import { Calendar } from './ui/calendar';
import './Booking.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Booking = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedService, setSelectedService] = useState('');
  const [services, setServices] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [holidayDates, setHolidayDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  
  const [formData, setFormData] = useState({
    studioName: '',
    photographerName: '',
    whatsappNumber: '',
    projectType: '',
    customProjectType: '',
    dataDeliveryMethod: '',
    projectNotes: '',
    customerType: ''
  });

  // Project types - fetched from Admin Panel services + "Other" option
  const getProjectTypes = () => {
    const serviceTypes = services.map(s => ({
      id: s.id,
      name: s.name,
      turnaround: s.turnaround_days === 1 ? '1 day' : `${s.turnaround_days} days`
    }));
    // Add "Other" option at the end
    return [...serviceTypes, { id: 'other', name: 'Other (Please Specify)', turnaround: 'TBD' }];
  };
  
  const projectTypes = getProjectTypes();

  // Data delivery methods
  const dataDeliveryMethods = [
    { id: 'hdd-courier', name: 'HDD via Courier', icon: HardDrive, description: 'Send your hard drive via courier service' },
    { id: 'online', name: 'Online Transfer', icon: Cloud, description: 'WeTransfer, Google Drive link, etc.' },
    { id: 'our-drive', name: 'Our Drive', icon: FolderSync, description: 'We will provide you our drive link' }
  ];

  // Fetch services and availability on mount
  useEffect(() => {
    fetchServices();
    fetchGeneralAvailability();
  }, []);

  // Fetch availability when service changes
  useEffect(() => {
    if (selectedService) {
      fetchAvailability();
    }
  }, [selectedService]);

  const fetchServices = async () => {
    try {
      const response = await fetch(`${API_URL}/api/services`);
      if (response.ok) {
        const data = await response.json();
        setServices(data);
        // Set first service as default for general availability
        if (data.length > 0) {
          setSelectedService(data[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch services:', error);
    }
  };

  const fetchGeneralAvailability = async () => {
    setCheckingAvailability(true);
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() + 1);
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + 60);

      // Fetch with a general service or first available
      const servicesRes = await fetch(`${API_URL}/api/services`);
      if (servicesRes.ok) {
        const servicesData = await servicesRes.json();
        if (servicesData.length > 0) {
          const response = await fetch(`${API_URL}/api/bookings/check-availability`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              service_id: servicesData[0].id,
              start_date: startDate.toISOString().split('T')[0],
              end_date: endDate.toISOString().split('T')[0]
            })
          });

          if (response.ok) {
            const data = await response.json();
            setBlockedDates((data.blocked_dates || []).map(d => new Date(d + 'T00:00:00')));
            setHolidayDates((data.holidays || []).map(d => new Date(d + 'T00:00:00')));
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch availability:', error);
    } finally {
      setCheckingAvailability(false);
    }
  };

  const fetchAvailability = async () => {
    if (!selectedService) return;
    
    setCheckingAvailability(true);
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() + 1);
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + 60);

      const response = await fetch(`${API_URL}/api/bookings/check-availability`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          service_id: selectedService,
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0]
        })
      });

      if (response.ok) {
        const data = await response.json();
        setBlockedDates((data.blocked_dates || []).map(d => new Date(d + 'T00:00:00')));
        setHolidayDates((data.holidays || []).map(d => new Date(d + 'T00:00:00')));
      }
    } catch (error) {
      console.error('Failed to fetch availability:', error);
    } finally {
      setCheckingAvailability(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleProjectTypeChange = (typeId) => {
    setFormData(prev => ({ ...prev, projectType: typeId }));
    // Map project type to service
    const serviceMapping = {
      'education-video': 'education-video',
      'reel': 'social-media-reel',
      'youtube-vlog': 'social-media-reel',
      'wedding-highlight': 'wedding-highlight',
      'wedding-short-film': 'short-film',
      'wedding-documentary': 'traditional-wedding-film',
      'wedding-teaser': 'wedding-teaser',
      'corporate-video': 'corporate-video',
      'ad-film': 'corporate-video',
      'documentary': 'documentary-film',
      'other': ''
    };
    
    const matchingService = services.find(s => s.slug === serviceMapping[typeId]);
    if (matchingService) {
      setSelectedService(matchingService.id);
    }
  };

  const validateStep = (step) => {
    switch(step) {
      case 1:
        return selectedDate;
      case 2:
        return formData.projectType && (formData.projectType !== 'other' || formData.customProjectType);
      case 3:
        return formData.studioName && formData.photographerName && formData.whatsappNumber;
      case 4:
        return formData.dataDeliveryMethod && formData.customerType;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep(4)) {
      setSubmitStatus({ type: 'error', message: 'Please fill all required fields' });
      return;
    }

    setLoading(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const projectTypeName = formData.projectType === 'other' 
        ? formData.customProjectType 
        : projectTypes.find(p => p.id === formData.projectType)?.name;

      const response = await fetch(`${API_URL}/api/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_name: `${formData.studioName} - ${formData.photographerName}`,
          client_email: `${formData.whatsappNumber}@placeholder.com`,
          client_phone: formData.whatsappNumber,
          service_id: selectedService || services[0]?.id,
          booking_date: selectedDate.toISOString().split('T')[0],
          message: `
Project Type: ${projectTypeName}
Data Delivery: ${dataDeliveryMethods.find(m => m.id === formData.dataDeliveryMethod)?.name}
Customer Type: ${formData.customerType === 'existing' ? 'Existing Customer' : 'New Customer'}
Notes: ${formData.projectNotes || 'None'}
          `.trim()
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitStatus({ 
          type: 'success', 
          message: '🎉 Booking request submitted successfully! We will contact you on WhatsApp within 24 hours.' 
        });
        setFormData({
          studioName: '',
          photographerName: '',
          whatsappNumber: '',
          projectType: '',
          customProjectType: '',
          dataDeliveryMethod: '',
          projectNotes: '',
          customerType: ''
        });
        setSelectedDate(null);
        setCurrentStep(1);
      } else {
        setSubmitStatus({ 
          type: 'error', 
          message: data.detail || 'Failed to submit booking. Please try again.' 
        });
      }
    } catch (error) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'Network error. Please check your connection and try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const selectedProjectType = projectTypes.find(p => p.id === formData.projectType);

  // Helper - compare two dates by Y-M-D
  const isSameDay = (d1, d2) => {
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getDate() === d2.getDate();
  };

  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Past dates (including today)
    if (date <= today) return true;
    
    // Current week not bookable
    const currentWeekStart = new Date(today);
    currentWeekStart.setDate(today.getDate() - today.getDay());
    const currentWeekEnd = new Date(currentWeekStart);
    currentWeekEnd.setDate(currentWeekStart.getDate() + 6);
    if (date >= currentWeekStart && date <= currentWeekEnd) return true;
    
    // Sunday closed
    if (date.getDay() === 0) return true;
    
    // Fully booked / blocked from existing bookings or 3-slot daily limit
    if (blockedDates.some(d => isSameDay(d, date))) return true;
    
    // Holidays
    if (holidayDates.some(d => isSameDay(d, date))) return true;
    
    return false;
  };

  // react-day-picker modifiers for visual styling
  const calendarModifiers = {
    booked: blockedDates,
    holiday: holidayDates,
  };

  const calendarModifiersClassNames = {
    booked: 'rdp-day_booked',
    holiday: 'rdp-day_holiday',
  };

  return (
    <section id="booking" className="booking section">
      <div className="container">
        <div className="booking-header">
          <h2 className="section-title">
            Book Your <span className="gradient-text">Editing Slot</span>
          </h2>
          <p className="section-subtitle">
            Check availability and book your slot in 4 simple steps
          </p>
        </div>

        {/* Progress Steps */}
        <div className="booking-progress">
          {[1, 2, 3, 4].map((step) => (
            <div key={step} className={`progress-step ${currentStep >= step ? 'active' : ''} ${currentStep > step ? 'completed' : ''}`}>
              <div className="step-number">{currentStep > step ? '✓' : step}</div>
              <span className="step-label">
                {step === 1 && 'Select Date'}
                {step === 2 && 'Project Type'}
                {step === 3 && 'Your Details'}
                {step === 4 && 'Delivery & Submit'}
              </span>
            </div>
          ))}
        </div>

        {submitStatus.message && (
          <div className={`booking-alert ${submitStatus.type}`} data-testid="booking-alert">
            {submitStatus.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
            <span>{submitStatus.message}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="booking-form-container" data-testid="booking-form">
          
          {/* Step 1: Date Selection (FIRST) */}
          {currentStep === 1 && (
            <div className="booking-step" data-testid="step-1">
              <h3 className="step-title">
                <CalendarIcon size={24} />
                Select Your Preferred Date
              </h3>
              <p className="step-description">Check our availability and choose a date that works for you</p>
              
              <div className="calendar-wrapper">
                {checkingAvailability && (
                  <div className="calendar-loading">
                    <Loader2 size={24} className="spin" />
                    <span>Loading availability...</span>
                  </div>
                )}
                
                <div className="calendar-container">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    className="booking-calendar"
                    disabled={isDateDisabled}
                    modifiers={calendarModifiers}
                    modifiersClassNames={calendarModifiersClassNames}
                    fromDate={new Date()}
                    data-testid="booking-calendar"
                  />
                </div>
                
                {selectedDate && (
                  <div className="selected-date-info" data-testid="selected-date-info">
                    <CheckCircle size={20} />
                    <div>
                      <p>Selected Date:</p>
                      <strong>{selectedDate.toLocaleDateString('en-IN', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}</strong>
                    </div>
                  </div>
                )}

                <div className="calendar-legend">
                  <div className="legend-item">
                    <span className="legend-dot available"></span>
                    <span>Available</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot blocked"></span>
                    <span>Fully Booked</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot holiday"></span>
                    <span>Holiday</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot sunday"></span>
                    <span>Sunday / Closed</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Project Type */}
          {currentStep === 2 && (
            <div className="booking-step" data-testid="step-2">
              <h3 className="step-title">
                <Video size={24} />
                Select Project Type
              </h3>
              
              <div className="project-type-grid">
                {projectTypes.map((type) => (
                  <div
                    key={type.id}
                    className={`project-type-card ${formData.projectType === type.id ? 'selected' : ''}`}
                    onClick={() => handleProjectTypeChange(type.id)}
                    data-testid={`project-type-${type.id}`}
                  >
                    <div className="project-type-name">{type.name}</div>
                    <div className="project-type-time">
                      <Clock size={14} />
                      <span>Est. {type.turnaround}</span>
                    </div>
                  </div>
                ))}
              </div>

              {formData.projectType === 'other' && (
                <div className="form-group" style={{marginTop: '1.5rem'}}>
                  <label htmlFor="customProjectType" className="form-label">
                    <span>Please specify your project type *</span>
                  </label>
                  <input
                    type="text"
                    id="customProjectType"
                    name="customProjectType"
                    className="form-input"
                    placeholder="Describe your project type"
                    value={formData.customProjectType}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              )}

              {selectedProjectType && selectedProjectType.id !== 'other' && (
                <div className="estimated-time-info">
                  <Info size={18} />
                  <span>Estimated turnaround time: <strong>{selectedProjectType.turnaround}</strong></span>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Your Details */}
          {currentStep === 3 && (
            <div className="booking-step" data-testid="step-3">
              <h3 className="step-title">
                <User size={24} />
                Your Details
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="studioName" className="form-label">
                    <Building2 size={18} />
                    <span>Studio / Company Name *</span>
                  </label>
                  <input
                    type="text"
                    id="studioName"
                    name="studioName"
                    className="form-input"
                    placeholder="Enter your studio or company name"
                    value={formData.studioName}
                    onChange={handleInputChange}
                    required
                    data-testid="studio-name-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="photographerName" className="form-label">
                    <User size={18} />
                    <span>Photographer / Contact Name *</span>
                  </label>
                  <input
                    type="text"
                    id="photographerName"
                    name="photographerName"
                    className="form-input"
                    placeholder="Enter your name"
                    value={formData.photographerName}
                    onChange={handleInputChange}
                    required
                    data-testid="photographer-name-input"
                  />
                </div>

                <div className="form-group full-width">
                  <label htmlFor="whatsappNumber" className="form-label">
                    <Phone size={18} />
                    <span>WhatsApp Number *</span>
                  </label>
                  <input
                    type="tel"
                    id="whatsappNumber"
                    name="whatsappNumber"
                    className="form-input"
                    placeholder="+91 98765 43210"
                    value={formData.whatsappNumber}
                    onChange={handleInputChange}
                    required
                    data-testid="whatsapp-input"
                  />
                  <span className="form-hint">We'll contact you on WhatsApp for project updates</span>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Data Delivery & Final Details */}
          {currentStep === 4 && (
            <div className="booking-step" data-testid="step-4">
              <h3 className="step-title">
                <Send size={24} />
                Data Delivery & Final Details
              </h3>
              
              <div className="form-section">
                <label className="form-label">
                  <HardDrive size={18} />
                  <span>How will you send the project data? *</span>
                </label>
                <div className="delivery-method-grid">
                  {dataDeliveryMethods.map((method) => {
                    const Icon = method.icon;
                    return (
                      <div
                        key={method.id}
                        className={`delivery-method-card ${formData.dataDeliveryMethod === method.id ? 'selected' : ''}`}
                        onClick={() => setFormData(prev => ({ ...prev, dataDeliveryMethod: method.id }))}
                        data-testid={`delivery-${method.id}`}
                      >
                        <Icon size={28} />
                        <div className="delivery-method-name">{method.name}</div>
                        <div className="delivery-method-desc">{method.description}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="form-section">
                <label className="form-label">
                  <UserCheck size={18} />
                  <span>Are you an existing customer or new? *</span>
                </label>
                <div className="customer-type-grid">
                  <div
                    className={`customer-type-card ${formData.customerType === 'existing' ? 'selected' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, customerType: 'existing' }))}
                    data-testid="customer-existing"
                  >
                    <UserCheck size={24} />
                    <span>Existing Customer</span>
                    <small>I've worked with RSE before</small>
                  </div>
                  <div
                    className={`customer-type-card ${formData.customerType === 'new' ? 'selected' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, customerType: 'new' }))}
                    data-testid="customer-new"
                  >
                    <UserPlus size={24} />
                    <span>New Customer</span>
                    <small>First time working together</small>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="projectNotes" className="form-label">
                  <MessageSquare size={18} />
                  <span>Additional Notes / Special Requirements</span>
                </label>
                <textarea
                  id="projectNotes"
                  name="projectNotes"
                  className="form-textarea"
                  placeholder="Any specific instructions, style preferences, or special requirements for your project..."
                  rows="4"
                  value={formData.projectNotes}
                  onChange={handleInputChange}
                  data-testid="project-notes-input"
                ></textarea>
              </div>

              {/* Summary */}
              <div className="booking-summary">
                <h4>Booking Summary</h4>
                <div className="summary-grid">
                  <div className="summary-item">
                    <span className="summary-label">Date:</span>
                    <span className="summary-value">
                      {selectedDate?.toLocaleDateString('en-IN', { 
                        weekday: 'short', 
                        day: 'numeric', 
                        month: 'short', 
                        year: 'numeric' 
                      })}
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Project:</span>
                    <span className="summary-value">
                      {formData.projectType === 'other' 
                        ? formData.customProjectType 
                        : projectTypes.find(p => p.id === formData.projectType)?.name}
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Studio:</span>
                    <span className="summary-value">{formData.studioName}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Contact:</span>
                    <span className="summary-value">{formData.photographerName}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">WhatsApp:</span>
                    <span className="summary-value">{formData.whatsappNumber}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Data Delivery:</span>
                    <span className="summary-value">
                      {dataDeliveryMethods.find(m => m.id === formData.dataDeliveryMethod)?.name}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="booking-navigation">
            {currentStep > 1 && (
              <button type="button" className="btn btn-secondary" onClick={prevStep}>
                ← Previous
              </button>
            )}
            
            {currentStep < 4 ? (
              <button 
                type="button" 
                className="btn btn-primary" 
                onClick={nextStep}
                disabled={!validateStep(currentStep)}
              >
                Next Step →
              </button>
            ) : (
              <button 
                type="submit" 
                className="btn btn-primary btn-submit"
                disabled={loading || !validateStep(4)}
                data-testid="booking-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="spin" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    <span>Submit Booking Request</span>
                  </>
                )}
              </button>
            )}
          </div>
        </form>

        {/* Info Box */}
        <div className="booking-info-box">
          <Info size={20} />
          <div>
            <strong>How to make changes later?</strong>
            <p>After submitting, you can contact us on WhatsApp to modify your booking details. We're always happy to accommodate changes!</p>
          </div>
        </div>
      </div>
    </section>
  );
};
